import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldCheck, Smartphone, Mail, Copy, MonitorX } from "lucide-react";

const Card = ({ title, icon: I, children, testid }) => (
  <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid={testid}>
    <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-4"><I size={18} /> {title}</h2>
    {children}
  </section>
);

function PasswordCard() {
  const [f, setF] = useState({ current: "", next: "", repeat: "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (f.next !== f.repeat) { toast.error("Die neuen Passwörter stimmen nicht überein"); return; }
    setBusy(true);
    try { await api.post("/auth/password", { current_password: f.current, new_password: f.next }); toast.success("Passwort geändert"); setF({ current: "", next: "", repeat: "" }); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const field = (k, l, tid) => <div><Label>{l}</Label><Input type="password" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} required className="mt-1" data-testid={tid} /></div>;
  return (
    <Card title="Passwort ändern" icon={KeyRound} testid="account-password-card">
      <form onSubmit={save} className="space-y-3 max-w-sm">
        {field("current", "Aktuelles Passwort", "account-pw-current")}
        {field("next", "Neues Passwort (mind. 10 Zeichen, Buchstaben + Ziffern/Sonderzeichen)", "account-pw-new")}
        {field("repeat", "Neues Passwort wiederholen", "account-pw-repeat")}
        <Button type="submit" disabled={busy} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="account-pw-save">Passwort speichern</Button>
      </form>
    </Card>
  );
}

function RecoveryCodes({ codes, onDone }) {
  const copy = () => { navigator.clipboard.writeText(codes.join("\n")); toast.success("Recovery-Codes kopiert"); };
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="account-recovery-codes">
      <p className="text-sm text-amber-900 font-semibold">Deine Recovery-Codes – jetzt sicher aufbewahren!</p>
      <p className="text-xs text-amber-800 mt-1">Jeder Code funktioniert genau einmal, falls Du keinen Zugriff auf App oder Mail hast. Sie werden nur jetzt angezeigt.</p>
      <div className="grid grid-cols-2 gap-1 mt-3 font-mono text-sm">{codes.map((c) => <div key={c} className="bg-white border border-amber-200 rounded px-2 py-1">{c}</div>)}</div>
      <div className="flex gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={copy} data-testid="account-recovery-copy"><Copy size={14} className="mr-1.5" /> Kopieren</Button>
        <Button size="sm" onClick={onDone} data-testid="account-recovery-done">Ich habe die Codes gesichert</Button>
      </div>
    </div>
  );
}

function Setup({ onEnabled }) {
  const [pw, setPw] = useState("");
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const start = async (method) => {
    try { const { data } = await api.post("/auth/mfa/setup", { method, password: pw }); setSetup(data); if (method === "email") toast.success(`Code an ${data.email} gesendet`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const enable = async () => {
    try { const { data } = await api.post("/auth/mfa/enable", { code }); onEnabled(data.recovery_codes); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (!setup) return (
    <div className="space-y-3 max-w-sm">
      <div><Label>Zur Bestätigung: aktuelles Passwort</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1" data-testid="account-mfa-password" /></div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={!pw} onClick={() => start("totp")} data-testid="account-mfa-setup-totp"><Smartphone size={15} className="mr-1.5" /> Mit Authenticator-App</Button>
        <Button variant="outline" disabled={!pw} onClick={() => start("email")} data-testid="account-mfa-setup-email"><Mail size={15} className="mr-1.5" /> Mit Code per Mail</Button>
      </div>
    </div>
  );
  return (
    <div className="space-y-3 max-w-md" data-testid="account-mfa-confirm">
      {setup.method === "totp" ? <>
        <p className="text-sm text-slate-600">Scanne den QR-Code mit Deiner Authenticator-App (z. B. Microsoft oder Google Authenticator) und gib den angezeigten Code ein.</p>
        <img src={setup.qr} alt="QR-Code" className="w-44 h-44 border rounded" data-testid="account-mfa-qr" />
        <p className="text-xs text-slate-500">Manuell: <span className="font-mono">{setup.secret}</span></p>
      </> : <p className="text-sm text-slate-600">Gib den Code ein, den wir an <b>{setup.email}</b> gesendet haben.</p>}
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6} placeholder="000000" className="w-36 text-center tracking-[0.3em]" data-testid="account-mfa-code" />
        <Button onClick={enable} disabled={code.length !== 6} className="bg-emerald-600 hover:bg-emerald-700" data-testid="account-mfa-enable">2FA aktivieren</Button>
        <Button variant="ghost" onClick={() => setSetup(null)}>Abbrechen</Button>
      </div>
    </div>
  );
}

function Active({ acc, onCodes, reload }) {
  const [pw, setPw] = useState("");
  const call = async (url, msg, cb) => {
    try { const { data } = await api.post(url, { password: pw }); toast.success(msg); setPw(""); cb?.(data); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const revoke = async () => { await api.post("/auth/trusted-devices/revoke"); toast.success("Alle vertrauten Browser entfernt"); reload(); };
  return (
    <div className="space-y-4">
      <div className="text-sm text-slate-700" data-testid="account-mfa-status"><span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold text-xs mr-2">aktiv</span>
        {acc.mfa_method === "totp" ? "Authenticator-App" : "Code per Mail"} · {acc.recovery_remaining} Recovery-Codes übrig · {acc.trusted_devices} vertraute(r) Browser</div>
      <div className="flex flex-wrap items-end gap-2">
        <div><Label>Aktuelles Passwort</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 w-56" data-testid="account-mfa-active-password" /></div>
        <Button variant="outline" disabled={!pw} onClick={() => call("/auth/mfa/recovery-codes", "Neue Recovery-Codes erstellt", (d) => onCodes(d.recovery_codes))} data-testid="account-mfa-new-codes">Neue Recovery-Codes</Button>
        <Button variant="outline" disabled={!pw} onClick={() => { if (window.confirm("2FA wirklich deaktivieren?")) call("/auth/mfa/disable", "2FA deaktiviert"); }} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="account-mfa-disable">2FA deaktivieren</Button>
      </div>
      {acc.trusted_devices > 0 && <Button size="sm" variant="ghost" onClick={revoke} data-testid="account-trusted-revoke"><MonitorX size={14} className="mr-1.5" /> Allen vertrauten Browsern das Vertrauen entziehen</Button>}
    </div>
  );
}

export default function AccountPage() {
  const [acc, setAcc] = useState(null);
  const [codes, setCodes] = useState(null);
  const load = useCallback(() => { api.get("/auth/account").then((r) => setAcc(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="p-6 lg:p-8 max-w-3xl space-y-6" data-testid="account-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Passwort & 2FA</h1>
        <p className="text-slate-500 mt-1">Sicherheit Deines Zugangs mit Passwort-Anmeldung.</p>
      </div>
      <PasswordCard />
      <Card title="Zwei-Faktor-Authentifizierung (2FA)" icon={ShieldCheck} testid="account-mfa-card">
        {codes ? <RecoveryCodes codes={codes} onDone={() => { setCodes(null); load(); }} />
          : !acc ? null : acc.mfa_enabled ? <Active acc={acc} onCodes={setCodes} reload={load} />
          : <><p className="text-sm text-slate-600 mb-4">Nach dem Passwort wird zusätzlich ein Code abgefragt. Beim Login kannst Du dem Browser 30 Tage vertrauen – dann entfällt nur der Code.</p><Setup onEnabled={(c) => { setCodes(c); load(); }} /></>}
      </Card>
    </div>
  );
}
